/**
 * Installation Guides Section
 * Handles filtering, sorting, and AJAX pagination
 */

class InstallationGuides {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.dataElement = document.getElementById(`installation-guides-data-${this.sectionId}`);
    this.contentContainer = document.getElementById(`installation-guides-content-${this.sectionId}`);
    this.gridContainer = document.getElementById(`installation-guides-grid-${this.sectionId}`);
    this.paginationContainer = document.getElementById(`installation-guides-pagination-${this.sectionId}`);
    
    if (!this.dataElement || !this.contentContainer) {
      return;
    }
    
    this.data = JSON.parse(this.dataElement.textContent);
    this.postsPerPage = this.data.postsPerPage;
    this.allGuides = this.data.guides || [];
    
    // Extract unique collections
    this.collections = this.extractUniqueCollections();
    
    // Current state
    this.currentFilter = this.data.currentFilter || 'all';
    this.currentSort = this.data.currentSort || 'most_recent';
    this.currentPage = this.data.currentPage || 1;
    
    // Filtered and sorted guides
    this.filteredGuides = [];
    this.sortedGuides = [];
    this.totalPages = 0;
    
    // DOM elements
    this.filterTabs = section.querySelectorAll('.installation-guides__filter-tab');
    this.sortSelect = section.querySelector(`#sort-select-${this.sectionId}`);
    this.filtersContainer = section.querySelector('.installation-guides__filters');
    
    this.init();
  }
  
  init() {
    // Read URL params if present
    this.readURLParams();
    
    // Apply initial filter and sort
    this.applyFilters();
    
    // Only render if we have guides data
    if (this.allGuides && this.allGuides.length > 0) {
      this.renderContent();
    } else {
      console.warn('No guides data found');
    }
    
    this.setupEventListeners();
    this.updateURL();
  }
  
  extractUniqueCollections() {
    const collections = new Set();
    this.allGuides.forEach(guide => {
      if (guide.collections && guide.collections.length > 0) {
        guide.collections.forEach(coll => {
          if (coll) {
            const trimmed = String(coll).trim();
            if (trimmed) collections.add(trimmed);
          }
        });
      }
    });
    return Array.from(collections).sort();
  }
  
  applyFilters() {
    // Filter by collection
    if (this.currentFilter === 'all') {
      this.filteredGuides = [...this.allGuides];
    } else {
      this.filteredGuides = this.allGuides.filter(guide => {
        if (!guide.collections || !Array.isArray(guide.collections)) {
          return false;
        }
        // Trim and compare collection names
        return guide.collections.some(coll => {
          const collName = String(coll || '').trim();
          const filterName = String(this.currentFilter || '').trim();
          return collName === filterName;
        });
      });
    }
    
    // Sort guides
    this.sortedGuides = [...this.filteredGuides];
    if (this.currentSort === 'alphabetical') {
      this.sortedGuides.sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
    } else {
      // Most recent - sort by created date (fallback to id)
      this.sortedGuides.sort((a, b) => {
        const dateA = this.getTimestamp(a.createdAt);
        const dateB = this.getTimestamp(b.createdAt);
        if (dateA === dateB) {
          const idA = parseInt(a.id) || 0;
          const idB = parseInt(b.id) || 0;
          return idB - idA;
        }
        return dateB - dateA;
      });
    }
    
    // Calculate total pages
    this.totalPages = Math.ceil(this.sortedGuides.length / this.postsPerPage);
    
    // Ensure current page is valid
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages || 1;
    }
  }
  
  getPaginatedGuides() {
    const startIndex = (this.currentPage - 1) * this.postsPerPage;
    const endIndex = startIndex + this.postsPerPage;
    return this.sortedGuides.slice(startIndex, endIndex);
  }
  
  formatDate(timestamp) {
    const millis = this.getTimestamp(timestamp);
    if (!millis) {
      return '';
    }
    const date = new Date(millis);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  getTimestamp(value) {
    if (!value) return 0;

    if (typeof value === 'number') {
      return value < 10000000000 ? value * 1000 : value;
    }

    if (typeof value === 'string') {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric < 10000000000 ? numeric * 1000 : numeric;
      }

      const isoCandidate = value.includes('T') ? value : `${value}T00:00:00`;
      const parsed = Date.parse(isoCandidate);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    if (typeof value === 'object' && value.value) {
      return this.getTimestamp(value.value);
    }

    return 0;
  }
  
  createCardElement(guide) {
    const card = document.createElement('article');
    card.className = 'installation-guides__card';
    
    const date = this.formatDate(guide.createdAt);
    const author = (guide.author && String(guide.author).trim()) || 'Access OMG';
    const title = guide.title || '';
    const excerpt = this.buildExcerpt(guide);
    const imageUrl = guide.image || '';
    const url = guide.url || '';
    const installationGuide = guide.installation_guide;
    const catalogue = guide.catalogue;
    const brochure = guide.brochure;
    const colour_chart = guide.colour_chart;
    
    const imageMarkup = imageUrl
      ? `
        <div class="installation-guides__card-image">
          <img src="${imageUrl}" alt="${this.escapeHtml(title)}" class="installation-guides__card-img" loading="lazy" width="928">
        </div>`
      : '';

    const titleMarkup = title
      ? `
        <h2 class="installation-guides__card-title">
          ${url ? `<a href="${url}">${this.escapeHtml(title)}</a>` : this.escapeHtml(title)}
        </h2>`
      : '';

    const excerptMarkup = excerpt
      ? `<p class="installation-guides__card-excerpt">${this.escapeHtml(excerpt)}</p>`
      : '';

    const downloadsMarkup = this.buildDownloadsList({
      installationGuide,
      catalogue,
      brochure,
      colour_chart
    });

    const metaMarkup = `
      <div class="installation-guides__card-meta">
        <div class="installation-guides__card-meta-item">
          <svg class="installation-guides__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_author" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
              <rect width="24" height="24" fill="#D9D9D9"/>
            </mask>
            <g mask="url(#mask0_author)">
              <path d="M6.023 17.2923C6.873 16.6616 7.799 16.1635 8.801 15.798C9.80283 15.4327 10.8692 15.25 12 15.25C13.1308 15.25 14.1972 15.4327 15.199 15.798C16.201 16.1635 17.127 16.6616 17.977 17.2923C18.5987 16.6089 19.0912 15.8179 19.4548 14.9192C19.8183 14.0206 20 13.0475 20 12C20 9.78333 19.2208 7.89583 17.6625 6.3375C16.1042 4.77917 14.2167 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 13.0475 4.18175 14.0206 4.54525 14.9192C4.90875 15.8179 5.40133 16.6089 6.023 17.2923ZM12.0003 12.75C11.0873 12.75 10.3173 12.4367 9.6905 11.81C9.0635 11.1832 8.75 10.4133 8.75 9.50025C8.75 8.58725 9.06333 7.81733 9.69 7.1905C10.3168 6.5635 11.0868 6.25 11.9998 6.25C12.9128 6.25 13.6827 6.56333 14.3095 7.19C14.9365 7.81683 15.25 8.58675 15.25 9.49975C15.25 10.4127 14.9367 11.1827 14.31 11.8095C13.6832 12.4365 12.9133 12.75 12.0003 12.75ZM12 21.5C10.6808 21.5 9.44333 21.2519 8.2875 20.7558C7.13167 20.2596 6.12625 19.5839 5.27125 18.7288C4.41608 17.8738 3.74042 16.8683 3.24425 15.7125C2.74808 14.5567 2.5 13.3192 2.5 12C2.5 10.6808 2.74808 9.44333 3.24425 8.2875C3.74042 7.13167 4.41608 6.12625 5.27125 5.27125C6.12625 4.41608 7.13167 3.74042 8.2875 3.24425C9.44333 2.74808 10.6808 2.5 12 2.5C13.3192 2.5 14.5567 2.74808 15.7125 3.24425C16.8683 3.74042 17.8738 4.41608 18.7288 5.27125C19.5839 6.12625 20.2596 7.13167 20.7558 8.2875C21.2519 9.44333 21.5 10.6808 21.5 12C21.5 13.3192 21.2519 14.5567 20.7558 15.7125C20.2596 16.8683 19.5839 17.8738 18.7288 18.7288C17.8738 19.5839 16.8683 20.2596 15.7125 20.7558C14.5567 21.2519 13.3192 21.5 12 21.5ZM12 20C12.9025 20 13.7727 19.8548 14.6105 19.5645C15.4483 19.274 16.1923 18.8679 16.8422 18.3462C16.1923 17.8436 15.458 17.4519 14.6395 17.1712C13.8208 16.8904 12.941 16.75 12 16.75C11.059 16.75 10.1776 16.8888 9.35575 17.1663C8.53392 17.4439 7.80125 17.8372 7.15775 18.3462C7.80775 18.8679 8.55167 19.274 9.3895 19.5645C10.2273 19.8548 11.0975 20 12 20ZM12 11.25C12.4975 11.25 12.9135 11.0827 13.248 10.748C13.5827 10.4135 13.75 9.9975 13.75 9.5C13.75 9.0025 13.5827 8.5865 13.248 8.252C12.9135 7.91733 12.4975 7.75 12 7.75C11.5025 7.75 11.0865 7.91733 10.752 8.252C10.4173 8.5865 10.25 9.0025 10.25 9.5C10.25 9.9975 10.4173 10.4135 10.752 10.748C11.0865 11.0827 11.5025 11.25 12 11.25Z" fill="#050505" fill-opacity="0.7"/>
            </g>
          </svg>
          <span class="installation-guides__card-author">${this.escapeHtml(author)}</span>
        </div>
      </div>`;

    card.innerHTML = `
      ${imageMarkup}
      <div class="installation-guides__card-content">
        ${metaMarkup}
        ${titleMarkup}
        ${excerptMarkup}
        ${downloadsMarkup}
      </div>
    `;
    return card;
  }

  buildExcerpt(guide) {
    // Handle both content (rich text object) and excerpt (string), limit to ~30 words
    let contentText = '';
    if (guide.excerpt) {
      if (typeof guide.excerpt === 'object' && guide.excerpt.value) {
        contentText = guide.excerpt.value;
      } else {
        contentText = String(guide.excerpt);
      }
    } else if (guide.content) {
      if (typeof guide.content === 'object' && guide.content.children) {
        contentText = this.extractTextFromRichText(guide.content);
      } else {
        contentText = String(guide.content).replace(/<[^>]*>/g, '');
      }
    }

    const words = contentText.split(/\s+/).filter(Boolean);
    if (words.length <= 30) return contentText;
    return `${words.slice(0, 30).join(' ')}...`;
  }

  buildDownloadsList(files) {
    const items = [];

    const buildItem = (label, href) => `
      <a href="${href}" class="installation-guides__item" target="_blank" rel="noopener noreferrer">
        <span class="installation-guides__item-title">
          ${this.escapeHtml(label)}
          ${this.getDownloadIcon()}
        </span>
      </a>`;

    if (files.installationGuide) {
      items.push(buildItem('Installation Guide', files.installationGuide));
    }
    if (files.catalogue) {
      items.push(buildItem('Catalogue', files.catalogue));
    }
    if (files.brochure) {
      items.push(buildItem('Brochure', files.brochure));
    }
     if (files.colour_chart) {
      items.push(buildItem('Colour Chart', files.colour_chart));
    }

    if (items.length === 0) return '';

    return `
      <div class="installation-guides__list">
        ${items.join('')}
      </div>
    `;
  }

  getDownloadIcon() {
    // Simple inline download icon similar to liquid inline asset
    return `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.5 0.5V10.3125L11.625 7.15625C11.8125 6.96875 12.1562 6.96875 12.3438 7.15625C12.5312 7.34375 12.5312 7.6875 12.3438 7.875L8.34375 11.875C8.15625 12.0625 7.8125 12.0625 7.625 11.875L3.625 7.875C3.4375 7.6875 3.4375 7.34375 3.625 7.15625C3.8125 6.96875 4.15625 6.96875 4.34375 7.15625L7.5 10.3125V0.5C7.5 0.25 7.71875 0 8 0C8.25 0 8.5 0.25 8.5 0.5ZM4.375 10L5.375 11H2C1.4375 11 1 11.4688 1 12V14C1 14.5625 1.4375 15 2 15H14C14.5312 15 15 14.5625 15 14V12C15 11.4688 14.5312 11 14 11H10.5938L11.5938 10H14C15.0938 10 16 10.9062 16 12V14C16 15.125 15.0938 16 14 16H2C0.875 16 0 15.125 0 14V12C0 10.9062 0.875 10 2 10H4.375ZM13.5 13C13.5 13.4375 13.1562 13.75 12.75 13.75C12.3125 13.75 12 13.4375 12 13C12 12.5938 12.3125 12.25 12.75 12.25C13.1562 12.25 13.5 12.5938 13.5 13Z" fill="black" fill-opacity="0.6"></path>
</svg>
    `;
  }
  
  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  extractTextFromRichText(node) {
    if (!node) return '';
    let text = '';
    if (node.children) {
      node.children.forEach(child => {
        if (child.type === 'text' && child.value) {
          text += child.value + ' ';
        } else if (child.children) {
          text += this.extractTextFromRichText(child) + ' ';
        }
      });
    }
    return text.trim();
  }
  
  renderContent() {
    if (!this.gridContainer) return;
    
    const guides = this.getPaginatedGuides();
    
    // Clear existing content
    this.gridContainer.innerHTML = '';
    
    // Render cards
    if (guides.length === 0) {
      this.gridContainer.innerHTML = '<div class="installation-guides__loading">No guides found.</div>';
    } else {
      guides.forEach(guide => {
        const card = this.createCardElement(guide);
        this.gridContainer.appendChild(card);
      });
    }
    
    // Render pagination
    this.renderPagination();
  }
  
  renderPagination() {
    if (!this.paginationContainer) return;
    
    if (this.totalPages <= 1) {
      this.paginationContainer.style.display = 'none';
      return;
    }
    
    this.paginationContainer.style.display = 'flex';
    
    // Update prev/next buttons
    const prevBtn = this.paginationContainer.querySelector('.installation-guides__pagination-btn--prev');
    const nextBtn = this.paginationContainer.querySelector('.installation-guides__pagination-btn--next');
    
    if (prevBtn) {
      prevBtn.disabled = this.currentPage === 1;
      prevBtn.dataset.page = this.currentPage - 1;
    }
    
    if (nextBtn) {
      nextBtn.disabled = this.currentPage >= this.totalPages;
      nextBtn.dataset.page = this.currentPage + 1;
    }
    
    // Render page numbers
    const numbersContainer = this.paginationContainer.querySelector('.installation-guides__pagination-numbers');
    if (!numbersContainer) return;
    
    numbersContainer.innerHTML = '';
    
    const pages = this.generatePaginationNumbers(this.currentPage, this.totalPages);
    
    pages.forEach(page => {
      if (page === 'ellipsis') {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'installation-guides__pagination-ellipsis';
        ellipsis.textContent = '...';
        numbersContainer.appendChild(ellipsis);
      } else {
        const number = document.createElement('button');
        number.type = 'button';
        number.className = 'installation-guides__pagination-number';
        if (page === this.currentPage) {
          number.classList.add('active');
        }
        number.textContent = page;
        number.dataset.page = page;
        number.addEventListener('click', () => {
          this.currentPage = page;
          this.updateContent();
        });
        numbersContainer.appendChild(number);
      }
    });
  }
  
  generatePaginationNumbers(currentPage, totalPages) {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 4) {
        // Show pages 2-5, then ellipsis, then last
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show first, ellipsis, then last 5 pages
        pages.push('ellipsis');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first, ellipsis, current-1, current, current+1, ellipsis, last
        pages.push('ellipsis');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }
    
    return pages;
  }
  
  async updateContent() {
    this.applyFilters();
    this.renderContent();
    this.updateURL();
    
    // Scroll to filters section
    if (this.filtersContainer) {
      this.filtersContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (this.section) {
      this.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
  
  async updateContentAjax() {
    // Show loading state
    if (this.gridContainer) {
      this.gridContainer.innerHTML = '<div class="installation-guides__loading">Loading...</div>';
    }
    
    try {
      // Build URL with parameters for Shopify section rendering
      const params = new URLSearchParams();
      if (this.currentFilter !== 'all') {
        params.set('filter', this.currentFilter);
      }
      if (this.currentSort !== 'most_recent') {
        params.set('sort', this.currentSort);
      }
      if (this.currentPage > 1) {
        params.set('page', this.currentPage);
      }
      params.set('section_id', `installation-guides`);
      
      // Use current page URL or construct section URL
      const baseUrl = window.location.pathname;
      const url = `${baseUrl}?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch content');
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract the section content - Shopify returns the section HTML
      const newSection = doc.querySelector(`[data-section-id="${this.sectionId}"]`) || 
                        doc.querySelector('.installation-guides');
      
      if (newSection) {
        const newGrid = newSection.querySelector(`#installation-guides-grid-${this.sectionId}`);
        const newPagination = newSection.querySelector(`#installation-guides-pagination-${this.sectionId}`);
        
        if (newGrid && this.gridContainer) {
          this.gridContainer.innerHTML = newGrid.innerHTML;
        }
        
        if (newPagination && this.paginationContainer) {
          this.paginationContainer.innerHTML = newPagination.innerHTML;
          this.paginationContainer.style.display = newPagination.style.display || 'flex';
          this.setupPaginationListeners();
        }
      } else {
        // Fallback to client-side rendering if section not found in response
        this.updateContent();
      }
    } catch (error) {
      console.error('Error updating content:', error);
      // Fallback to client-side rendering
      this.updateContent();
    }
  }
  
  updateURL() {
    const params = new URLSearchParams();
    if (this.currentFilter !== 'all') {
      params.set('filter', this.currentFilter);
    }
    if (this.currentSort !== 'most_recent') {
      params.set('sort', this.currentSort);
    }
    if (this.currentPage > 1) {
      params.set('page', this.currentPage);
    }
    
    const newURL = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    
    window.history.pushState({}, '', newURL);
  }
  
  setupEventListeners() {
    // Filter tabs
    if (this.filterTabs && this.filterTabs.length > 0) {
      this.filterTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const filter = tab.dataset.filter;
          if (filter !== this.currentFilter) {
            this.currentFilter = filter;
            this.currentPage = 1;
            
            // Update active state
            this.filterTabs.forEach(t => {
              t.classList.remove('active');
              t.setAttribute('aria-selected', 'false');
            });
            tab.classList.add('active');
            tab.setAttribute('aria-selected', 'true');
            
            this.updateContent();
          }
        });
      });
    }
    
    // Sort select
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => {
        const sort = e.target.value;
        if (sort !== this.currentSort) {
          this.currentSort = sort;
          this.currentPage = 1;
          this.updateContent();
        }
      });
    }
    
    // Pagination buttons
    this.setupPaginationListeners();
    
    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.readURLParams();
      this.updateContent();
    });
  }
  
  setupPaginationListeners() {
    if (!this.paginationContainer) return;
    
    const prevBtn = this.paginationContainer.querySelector('.installation-guides__pagination-btn--prev');
    const nextBtn = this.paginationContainer.querySelector('.installation-guides__pagination-btn--next');
    
    if (prevBtn) {
      prevBtn.replaceWith(prevBtn.cloneNode(true));
      const newPrevBtn = this.paginationContainer.querySelector('.installation-guides__pagination-btn--prev');
      newPrevBtn.addEventListener('click', () => {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.updateContent();
        }
      });
    }
    
    if (nextBtn) {
      nextBtn.replaceWith(nextBtn.cloneNode(true));
      const newNextBtn = this.paginationContainer.querySelector('.installation-guides__pagination-btn--next');
      newNextBtn.addEventListener('click', () => {
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
          this.updateContent();
        }
      });
    }
  }
  
  readURLParams() {
    const params = new URLSearchParams(window.location.search);
    this.currentFilter = params.get('filter') || 'all';
    this.currentSort = params.get('sort') || 'most_recent';
    this.currentPage = parseInt(params.get('page')) || 1;
    
    // Update UI to match URL params (after tabs are built)
    if (this.filterTabs && this.filterTabs.length > 0) {
      this.filterTabs.forEach(tab => {
        if (tab.dataset.filter === this.currentFilter) {
          tab.classList.add('active');
          tab.setAttribute('aria-selected', 'true');
        } else {
          tab.classList.remove('active');
          tab.setAttribute('aria-selected', 'false');
        }
      });
    }
    
    if (this.sortSelect) {
      this.sortSelect.value = this.currentSort;
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.installation-guides[data-section-id]');
  
  sections.forEach(section => {
    new InstallationGuides(section);
  });
});

// Re-initialize on section load (for theme editor)
if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.installation-guides[data-section-id]');
    if (section) {
      new InstallationGuides(section);
    }
  });
}

